import { create } from 'zustand';
import { supabase } from '../supabase';

interface HospitalProfile {
  id?: string;
  logoUrl: string;
  name: string;
  phone1: string;
  phone2: string;
  phone3: string;
  email: string;
  address: string;
  website: string;
  description: string;
  usePrescriptionHeader?: boolean;
  wallpaperUrl?: string;
  wallpaperOpacity?: number;
  wallpaperUpdatedAt?: string;
}

interface HospitalProfileStore {
  profile: HospitalProfile;
  isLoading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (profile: Partial<HospitalProfile>) => Promise<void>;
  uploadWallpaper: (file: File) => Promise<string>;
  updateWallpaper: (wallpaperUrl: string, opacity?: number) => Promise<void>;
  removeWallpaper: () => Promise<void>;
}

const defaultProfile: HospitalProfile = {
  logoUrl: "",
  name: "AMCare Medical Center",
  phone1: "+1 (555) 123-4567",
  phone2: "+1 (555) 123-4568",
  phone3: "+1 (555) 123-4569",
  email: "info@amcare.example.com",
  address: "123 Medical Plaza, Healthcare City",
  website: "https://amcare.example.com",
  description: "Your trusted healthcare partner",
  usePrescriptionHeader: true
};

export const useHospitalProfileStore = create<HospitalProfileStore>()((set, get) => ({
  profile: defaultProfile,
  isLoading: false,
  error: null,

  fetchProfile: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('clinic_settings')
        .select('id, logo_url, name, phone, phone_secondary, phone_emergency, email, address, website, description, use_prescription_header, wallpaper_url, wallpaper_opacity, wallpaper_updated_at, created_at')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching clinic settings:', error);
        throw error;
      }

      if (data && data.length > 0) {
        const profileData = data[0]; // Get the first (most recent) profile
        // Map database fields to our interface
        const mappedProfile: HospitalProfile = {
          id: profileData.id,
          logoUrl: profileData.logo_url || "",
          name: profileData.name || defaultProfile.name,
          phone1: profileData.phone || defaultProfile.phone1,
          phone2: profileData.phone_secondary || "",
          phone3: profileData.phone_emergency || "",
          email: profileData.email || defaultProfile.email,
          address: profileData.address || defaultProfile.address,
          website: profileData.website || defaultProfile.website,
          description: profileData.description || defaultProfile.description,
          usePrescriptionHeader: profileData.use_prescription_header ?? true,
          wallpaperUrl: profileData.wallpaper_url,
          wallpaperOpacity: profileData.wallpaper_opacity || 0.2,
          wallpaperUpdatedAt: profileData.wallpaper_updated_at
        };
        set({ profile: mappedProfile, isLoading: false });
      } else {
        // No profile exists, use defaults
        set({ profile: defaultProfile, isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching clinic profile:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false,
        profile: defaultProfile 
      });
    }
  },

  updateProfile: async (newProfile) => {
    set({ isLoading: true, error: null });
    try {
      const currentProfile = get().profile;
      
      // Prepare data for database (map our interface to DB fields)
      const dbData = {
        name: newProfile.name || currentProfile.name,
        address: newProfile.address || currentProfile.address,
        phone: newProfile.phone1 || currentProfile.phone1,
        phone_secondary: newProfile.phone2 ?? currentProfile.phone2 ?? null,
        phone_emergency: newProfile.phone3 ?? currentProfile.phone3 ?? null,
        email: newProfile.email || currentProfile.email,
        website: newProfile.website || currentProfile.website,
        description: newProfile.description || currentProfile.description,
        logo_url: newProfile.logoUrl || currentProfile.logoUrl,
        use_prescription_header: newProfile.usePrescriptionHeader ?? currentProfile.usePrescriptionHeader ?? true,
        updated_at: new Date().toISOString()
      } as any;

      if (currentProfile.id) {
        // Update existing profile
        const { data, error } = await supabase
          .from('clinic_settings')
          .update(dbData)
          .eq('id', currentProfile.id)
          .select()
          .single();

        if (error) throw error;

        // Update local state
        set((state) => ({
          profile: { ...state.profile, ...newProfile, id: data.id },
          isLoading: false
        }));
      } else {
        // Create new profile
        const { data, error } = await supabase
          .from('clinic_settings')
          .insert([dbData])
          .select()
          .single();

        if (error) throw error;

        // Update local state
        set((state) => ({
          profile: { ...state.profile, ...newProfile, id: data.id },
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Error updating clinic profile:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false 
      });
    }
  },

  uploadWallpaper: async (file: File) => {
    set({ isLoading: true, error: null });
    try {
      // Validate file
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }
      
      if (!allowedTypes.includes(file.type)) {
        throw new Error('File must be a JPEG, PNG, or WebP image');
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `wallpaper-${Date.now()}.${fileExt}`;

      // File upload validation completed

      // Get Supabase URL and headers
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const uploadUrl = `${supabaseUrl}/storage/v1/object/wallpapers/${fileName}`;

      // Upload using fetch API
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': file.type,
        },
        body: file
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Upload error:', errorData);
        throw new Error(`Upload failed: ${errorData.message || response.statusText}`);
      }

      const uploadData = await response.json();

      // Get public URL
      const publicUrl = `${supabaseUrl}/storage/v1/object/public/wallpapers/${fileName}`;

      set({ isLoading: false });
      return publicUrl;
    } catch (error) {
      console.error('Error uploading wallpaper:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false 
      });
      throw error;
    }
  },

  updateWallpaper: async (wallpaperUrl: string, opacity: number = 0.2) => {
    set({ isLoading: true, error: null });
    try {
      const currentProfile = get().profile;
      
      const dbData = {
        wallpaper_url: wallpaperUrl,
        wallpaper_opacity: opacity,
        wallpaper_updated_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (currentProfile.id) {
        // Update existing profile
        const { error } = await supabase
          .from('clinic_settings')
          .update(dbData)
          .eq('id', currentProfile.id);

        if (error) throw error;

        // Update local state
        set((state) => ({
          profile: { 
            ...state.profile, 
            wallpaperUrl, 
            wallpaperOpacity: opacity,
            wallpaperUpdatedAt: dbData.wallpaper_updated_at
          },
          isLoading: false
        }));
      } else {
        throw new Error('No profile found. Please create a profile first.');
      }
    } catch (error) {
      console.error('Error updating wallpaper:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false 
      });
      throw error;
    }
  },

  removeWallpaper: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentProfile = get().profile;
      
      const dbData = {
        wallpaper_url: null,
        wallpaper_opacity: null,
        wallpaper_updated_at: null,
        updated_at: new Date().toISOString()
      };

      if (currentProfile.id) {
        // Update existing profile
        const { error } = await supabase
          .from('clinic_settings')
          .update(dbData)
          .eq('id', currentProfile.id);

        if (error) throw error;

        // Update local state
        set((state) => ({
          profile: { 
            ...state.profile, 
            wallpaperUrl: undefined, 
            wallpaperOpacity: undefined,
            wallpaperUpdatedAt: undefined
          },
          isLoading: false
        }));
      } else {
        throw new Error('No profile found. Please create a profile first.');
      }
    } catch (error) {
      console.error('Error removing wallpaper:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false 
      });
      throw error;
    }
  }
})); 